/**
 * Der letzte Schritt des Beratermodus — und der Ersatz für die frühere Seite
 * `/contacts/:id/auswertung`.
 *
 * **Was hier passiert und warum es hierher gehört.** Das Absenden des
 * Fragebogens legt nur einen Entwurf am Kontakt ab
 * (`risikoanalyseAuswertungDraftData`). Erst das Bestätigen schreibt die
 * Angaben in die strukturierten Kontaktfelder, legt die Detail-Risiken an und
 * friert den Antwortsatz als `risikoanalyseAuswertungData` ein. Bis zu diesem
 * Stand lag dieser zweite Schritt auf einer eigenen Seite, die man über ein
 * Startfenster finden musste — wer sie nicht kannte, hatte seine Analyse nie
 * übernommen, und der Kontakt blieb leer, obwohl der Fragebogen vollständig
 * war. Deshalb steht der Abschluss jetzt dort, wo die Arbeit ohnehin endet.
 *
 * Die Seite bot zusätzlich alle Felder ein zweites Mal zum Bearbeiten an.
 * Das entfällt ersatzlos: Korrigiert wird im Fragebogen selbst, dessen
 * Zusammenfassung („Antworten prüfen") zwei Schritte vorher steht und in jeden
 * Abschnitt springt. Zwei Oberflächen für dieselbe Angabe waren der Grund,
 * warum Kurz- und Langform-Keys überhaupt auseinanderlaufen konnten.
 */
import * as React from "react";
import {
  Button,
  makeStyles,
  mergeClasses,
  shorthands,
  Toast,
  ToastTitle,
  Toaster,
  useId,
  useToastController,
} from "@fluentui/react-components";
import { apiUrl } from "../../utils/api";
import { BRAND } from "./tokens";
import { sectionColorHex } from "./sectionColor";
import {
  DETAIL_ANALYSES,
  groupTriggeredDetailAnalysesByBereich,
  isDetailAnalysisTriggered,
} from "./detailAnalyses";
import { stepFormSchema } from "./schema";
import {
  dokumentKeysDesSchemas,
  holeAnalyseUploadsMitFrist,
  mitServerDokumenten,
} from "../../services/risikoanalyseUploadService";
import type { Answers } from "./types";
import {
  kandidatenAusAntworten,
  kandidatSchluessel,
  QUELLE_TITEL,
  type UebernahmeKandidat,
} from "./uebernahmeKandidaten";
import { buildCompanyContactCreatePayload } from "../../utils/buildCompanyContactCreatePayload";
import { buildPrivateContactCreatePayload } from "../../utils/buildPrivateContactCreatePayload";
import { resolvePersonTypeId } from "../../utils/personTypeId";
import type { FormState } from "../../utils/formUtils";

const useStyles = makeStyles({
  wrap: { display: "grid", rowGap: "16px" },
  karte: {
    backgroundColor: BRAND.cardBg,
    ...shorthands.border("1px", "solid", BRAND.border),
    ...shorthands.borderRadius("10px"),
    ...shorthands.padding("14px", "16px"),
  },
  karteHinweis: {
    backgroundColor: "#f4f9ff",
    ...shorthands.border("1px", "solid", "#bcd6f5"),
  },
  kopf: {
    margin: 0,
    marginBottom: "8px",
    fontFamily: BRAND.fontAccent,
    fontSize: "1.05rem",
    fontWeight: 400,
    color: BRAND.ink,
  },
  beschreibung: {
    margin: 0,
    marginBottom: "12px",
    color: BRAND.muted,
    fontSize: "0.85rem",
    lineHeight: 1.45,
  },
  zeile: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    columnGap: "12px",
    flexWrap: "wrap",
    rowGap: "8px",
    paddingTop: "8px",
    paddingBottom: "8px",
    ...shorthands.borderTop("1px", "dashed", BRAND.borderDashed),
  },
  zeileInfo: {
    display: "flex",
    flexDirection: "column",
    rowGap: "2px",
    fontSize: "0.9rem",
    minWidth: 0,
  },
  firma: { fontWeight: 600, color: BRAND.ink },
  meta: { fontSize: "0.8rem", color: BRAND.muted },
  erledigt: { fontSize: "0.85rem", color: "#1f7a3c", fontWeight: 600 },
  analyseZeile: {
    display: "flex",
    alignItems: "center",
    columnGap: "10px",
    paddingTop: "6px",
    paddingBottom: "6px",
    fontSize: "0.9rem",
    color: BRAND.ink,
  },
  badge: {
    ...shorthands.padding("2px", "9px"),
    ...shorthands.borderRadius("999px"),
    color: BRAND.white,
    fontSize: "0.72rem",
    fontWeight: 600,
    whiteSpace: "nowrap",
  },
  leer: { margin: 0, color: BRAND.muted, fontSize: "0.88rem" },
  linkZeile: {
    display: "flex",
    alignItems: "center",
    columnGap: "10px",
    flexWrap: "wrap",
    rowGap: "8px",
    marginTop: "12px",
    paddingTop: "12px",
    ...shorthands.borderTop("1px", "dashed", BRAND.borderDashed),
  },
  linkWert: {
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
    fontSize: "0.8rem",
    color: BRAND.purple,
    wordBreak: "break-all",
    minWidth: 0,
  },
  abschluss: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    columnGap: "12px",
    flexWrap: "wrap",
    rowGap: "8px",
  },
  bestaetigt: {
    fontSize: "0.88rem",
    color: "#1f7a3c",
    fontWeight: 600,
  },
  aktionenRechts: { marginTop: "12px", textAlign: "right" },
});

export type AuswertungAbschlussProps = {
  answers: Answers;
  /** Beratermodus-Kontakt. Ohne ihn gibt es nichts zu bestätigen. */
  contactId: number | null;
  /** Schreibt eine Änderung zurück in den Antwortsatz des Formulars. */
  onChange: (key: string, value: unknown) => void;
  /**
   * Bereits bestätigt (der Kontakt trägt ein `risikoanalyseAuswertungAt`).
   * Kommt vom Formular, das den Zustand beim Laden mitbekommt.
   */
  bereitsBestaetigt?: boolean;
};

const AuswertungAbschluss = ({
  answers,
  contactId,
  onChange,
  bereitsBestaetigt = false,
}: AuswertungAbschlussProps) => {
  const styles = useStyles();
  const toasterId = useId("auswertung-abschluss-toaster");
  const { dispatchToast } = useToastController(toasterId);

  const [speichert, setSpeichert] = React.useState(false);
  const [bestaetigt, setBestaetigt] = React.useState(bereitsBestaetigt);
  const [uebernimmtId, setUebernimmtId] = React.useState<string | null>(null);
  const [linkLaeuft, setLinkLaeuft] = React.useState(false);
  const [detailLink, setDetailLink] = React.useState<string | null>(null);

  React.useEffect(() => setBestaetigt(bereitsBestaetigt), [bereitsBestaetigt]);

  const melde = (text: string, intent: "success" | "error") =>
    dispatchToast(
      <Toast>
        <ToastTitle>{text}</ToastTitle>
      </Toast>,
      { intent }
    );

  /**
   * Firmen aus der Analyse, die ein eigener Kontakt werden können: Verbund,
   * Beteiligungen und externe Dienstleister. Bis hierher waren nur die
   * Dienstleister übernehmbar — der Verbund blieb im Antwortsatz stehen.
   */
  const kandidaten: UebernahmeKandidat[] = React.useMemo(
    () => kandidatenAusAntworten(answers),
    [answers]
  );

  const ausgeloest = React.useMemo(() => {
    const gruppiert = groupTriggeredDetailAnalysesByBereich(answers);
    const flach: Array<{ key: string; title: string; bereich: 1 | 2 | 3 | 4 }> = [];
    (Object.keys(gruppiert) as unknown as Array<1 | 2 | 3 | 4>).forEach((b) => {
      for (const d of gruppiert[b]) flach.push({ key: d.key, title: d.title, bereich: d.bereich });
    });
    return flach;
  }, [answers]);

  const uebernimm = async (kandidat: UebernahmeKandidat) => {
    if (kandidat.uebernommenAlsContactId) return;
    const felder = kandidat.felder as unknown as FormState;

    /*
     * Firmen und Personen haben je einen eigenen Payload-Bauer — verschiedene
     * Pflichtfelder, verschiedene Kommunikationswege, verschiedener Kontakttyp.
     * Die Person braucht ihre Typ-Id ausdrücklich: Ohne sie entstünde ein
     * Kontakt ohne Typ.
     */
    let koerper: Record<string, unknown> | null = null;
    if (kandidat.art === "firma") {
      koerper = buildCompanyContactCreatePayload(felder)?.payloadWithDetails ?? null;
    } else {
      koerper = buildPrivateContactCreatePayload(felder, await resolvePersonTypeId());
    }
    if (!koerper) {
      melde("Der Kontakt konnte aus diesen Angaben nicht gebildet werden.", "error");
      return;
    }
    setUebernimmtId(kandidatSchluessel(kandidat));
    try {
      const res = await fetch(apiUrl("contact"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(koerper),
      });
      if (!res.ok) {
        melde(`Übernahme fehlgeschlagen: ${await res.text()}`, "error");
        return;
      }
      const erzeugt = await res.json();
      const neueId: string | undefined =
        erzeugt?.id != null ? String(erzeugt.id) : erzeugt?.contactId;
      if (!neueId) {
        melde("Das Backend lieferte keine Kontakt-ID zurück.", "error");
        return;
      }
      /*
       * Die Kontakt-Nummer landet im Antwortsatz — an genau dem Eintrag, aus
       * dem sie stammt. Das Bestätigen liest sie von dort und legt daraus die
       * Firmenverbindung an; ohne sie entsteht keine Kante.
       */
      const bisher = Array.isArray(answers[kandidat.quelle])
        ? (answers[kandidat.quelle] as Array<{ id: string }>)
        : [];
      const zeitFeld =
        kandidat.zielFeld === "personContactId" ? "personUebernommenAt" : "uebernommenAt";
      const aktualisiert = bisher.map((e) =>
        e.id === kandidat.id
          ? { ...e, [kandidat.zielFeld]: neueId, [zeitFeld]: new Date().toISOString() }
          : e
      );
      onChange(kandidat.quelle, aktualisiert);
      melde(
        `${kandidat.bezeichnung} als ${kandidat.art === "firma" ? "Firmen" : "Personen"}-Kontakt übernommen.`,
        "success"
      );
    } finally {
      setUebernimmtId(null);
    }
  };

  const uebernimmAlle = async () => {
    for (const kandidat of kandidaten) {
      if (!kandidat.uebernommenAlsContactId) {
        // eslint-disable-next-line no-await-in-loop
        await uebernimm(kandidat);
      }
    }
  };

  const bestaetige = async () => {
    if (contactId == null) return;
    setSpeichert(true);
    try {
      /*
       * Unmittelbar vor dem Schreiben noch einmal die Dateiliste des Servers —
       * denn was hier rausgeht, ist endgültig: `applyRisikoanalyseAuswertung`
       * legt `acceptedAnswers` 1:1 nach `risikoanalyseAuswertungData`, und von
       * dort gewinnt es beim nächsten Öffnen gegen jeden Entwurf. Eine seit dem
       * Laden hinzugekommene Bilanz fehlte sonst dauerhaft.
       *
       * Fällt der Abruf aus, wird NICHT bestätigt: Ein „gespeichert" mit einer
       * Dateiliste, die niemand geprüft hat, ist schlimmer als ein zweiter
       * Anlauf — rückgängig macht es danach nichts mehr.
       */
      let zuSpeichern = answers;
      try {
        const { dateien } = await holeAnalyseUploadsMitFrist(contactId);
        zuSpeichern = mitServerDokumenten(
          answers,
          dokumentKeysDesSchemas(stepFormSchema),
          dateien
        );
      } catch {
        melde(
          "Die hochgeladenen Unterlagen konnten nicht geprüft werden — bitte erneut versuchen. Es wurde nichts gespeichert.",
          "error"
        );
        return;
      }

      const res = await fetch(apiUrl(`contact/${contactId}/analysemodus/auswertung`), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          acceptedAnswers: zuSpeichern,
          triggeredDetailAnalyses: ausgeloest,
        }),
      });
      if (res.ok) {
        setBestaetigt(true);
        /*
         * Eine gescheiterte Firmenverbindung bricht die Bestätigung bewusst
         * nicht ab — gemeldet werden muss sie trotzdem, sonst fehlt sie im
         * Organigramm und niemand weiß davon.
         */
        let ergebnis: {
          verbindungen?: { angelegt: number; fehlgeschlagen: number };
        } | null = null;
        try {
          ergebnis = await res.json();
        } catch {
          // Eine unlesbare Antwort ändert nichts daran, dass gespeichert
          // wurde — dann eben ohne den Hinweis auf die Verbindungen.
        }
        const offen = ergebnis?.verbindungen?.fehlgeschlagen ?? 0;
        if (offen > 0) {
          melde(
            `Auswertung übernommen. ${offen === 1 ? "Eine Firmenverbindung" : `${offen} Firmenverbindungen`} ` +
              "konnte nicht angelegt werden — bitte im Reiter Risiken prüfen.",
            "error"
          );
        } else {
          melde("Auswertung übernommen — die Angaben stehen jetzt am Kontakt.", "success");
        }
      } else {
        melde(`Fehler: ${await res.text()}`, "error");
      }
    } finally {
      setSpeichert(false);
    }
  };

  const erzeugeDetailLink = async () => {
    if (contactId == null) return;
    setLinkLaeuft(true);
    try {
      const res = await fetch(apiUrl(`contact/${contactId}/risikoanalyse-detail-link`), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ triggered: ausgeloest }),
      });
      if (res.ok) {
        const data = await res.json();
        setDetailLink(data?.url ?? null);
        melde("Link zur Detail-Erfassung erzeugt.", "success");
      } else {
        melde(`Link konnte nicht erzeugt werden: ${await res.text()}`, "error");
      }
    } finally {
      setLinkLaeuft(false);
    }
  };

  const kopiereLink = () => {
    if (!detailLink) return;
    navigator.clipboard.writeText(detailLink).then(() => melde("Link kopiert.", "success"));
  };

  const offeneUebernahmen = kandidaten.some((k) => !k.uebernommenAlsContactId);

  return (
    <div className={styles.wrap}>
      <Toaster toasterId={toasterId} />

      {kandidaten.length > 0 && (
        <div className={mergeClasses(styles.karte, styles.karteHinweis)}>
          <h3 className={styles.kopf}>
            Aus der Analyse als Kontakte übernehmen ({kandidaten.length})
          </h3>
          <p className={styles.beschreibung}>
            Verbundene Unternehmen, Beteiligungen, Geschäftsführung, Gesellschafter und
            Zuständige aus dieser Analyse. „Übernehmen" legt sie als Kontakt an. Beim
            Bestätigen entsteht daraus die Verbindung zum Unternehmen — mit der erfassten
            Rolle und, wo erfasst, der Beteiligungsquote.
          </p>
          {kandidaten.map((kandidat) => {
            const laeuft = uebernimmtId === kandidatSchluessel(kandidat);
            const fertig = !!kandidat.uebernommenAlsContactId;
            return (
              <div key={kandidatSchluessel(kandidat)} className={styles.zeile}>
                <div className={styles.zeileInfo}>
                  <span className={styles.firma}>{kandidat.bezeichnung}</span>
                  <span className={styles.meta}>
                    {[QUELLE_TITEL[kandidat.quelle], ...kandidat.merkmale].join(" · ")}
                  </span>
                </div>
                {fertig ? (
                  <span className={styles.erledigt}>✓ übernommen</span>
                ) : (
                  <Button
                    appearance="primary"
                    disabled={laeuft || uebernimmtId !== null}
                    onClick={() => uebernimm(kandidat)}
                  >
                    {laeuft ? "Übernehme…" : "Übernehmen"}
                  </Button>
                )}
              </div>
            );
          })}
          {offeneUebernahmen && (
            <div className={styles.aktionenRechts}>
              <Button
                appearance="secondary"
                disabled={uebernimmtId !== null}
                onClick={uebernimmAlle}
              >
                Alle übernehmen
              </Button>
            </div>
          )}
        </div>
      )}

      <div className={styles.karte}>
        <h3 className={styles.kopf}>Empfohlene Detailanalysen ({ausgeloest.length})</h3>
        {ausgeloest.length === 0 ? (
          <p className={styles.leer}>
            Aus den Antworten ergibt sich keine vertiefende Analyse.
          </p>
        ) : (
          DETAIL_ANALYSES.filter((d) => isDetailAnalysisTriggered(d, answers)).map((d) => (
            <div key={d.key} className={styles.analyseZeile}>
              <span
                className={styles.badge}
                style={{ backgroundColor: sectionColorHex[d.color] }}
              >
                {d.bereich}
              </span>
              <span>{d.title}</span>
            </div>
          ))
        )}
        {/*
          * Der Link entsteht erst nach dem Bestätigen. Vorher zeigte er auf
          * eine Erfassung, deren ausgelöste Analysen sich mit der nächsten
          * Korrektur noch ändern könnten — der Kunde bekäme einen Fragebogen zu
          * Risiken, die am Ende gar nicht in seiner Auswertung stehen.
          */}
        {bestaetigt && ausgeloest.length > 0 && (
          <div className={styles.linkZeile}>
            {!detailLink ? (
              <Button appearance="primary" disabled={linkLaeuft} onClick={erzeugeDetailLink}>
                {linkLaeuft ? "Wird erzeugt…" : "Detail-Erfassung-Link für den Kunden erzeugen"}
              </Button>
            ) : (
              <>
                <span className={styles.meta}>Link für den Kunden:</span>
                <span className={styles.linkWert}>{detailLink}</span>
                <Button onClick={kopiereLink}>Link kopieren</Button>
              </>
            )}
          </div>
        )}
      </div>

      <div className={styles.abschluss}>
        {bestaetigt && (
          <span className={styles.bestaetigt}>
            ✓ Übernommen — erneutes Bestätigen aktualisiert den Stand am Kontakt.
          </span>
        )}
        <Button
          appearance="primary"
          disabled={speichert || contactId == null}
          onClick={bestaetige}
        >
          {speichert ? "Übernehme…" : bestaetigt ? "Erneut übernehmen" : "Bestätigen & speichern"}
        </Button>
      </div>
    </div>
  );
};

export default AuswertungAbschluss;
